// Port of shelf_test/chart/items_list.coffee — the per-product metrics table under the
// chart. Audited against the ItemsListView base it used to extend: of that base's behaviour
// it used only `onLabelClick`, which the block shim supplies as a callback (it reaches into
// the filters sidebar, which is page-level).
//
// It opens in the order the report holds -- the one the chart is drawn in -- and every column
// sorts from there. A sort the researcher picks names an order back, which the block settles on
// and the shell writes down, so the chart follows the table and the report remembers it.
//
// The ecommerce question renders a superset of this table; the trait breakdown is shared via
// sh/lib/answer-trait-table.

import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'
import { byCellValue, byPosition } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { DEFAULT_SORT_ORDER } from '@aytm/app/stats/page/question-views/sh/lib/report-params'
import { calcTraitTable } from '@aytm/app/stats/page/question-views/sh/lib/answer-trait-table'
import { getTraitValues } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/traitValues'

// Palette length: answer-variant colour indices cycle modulo this size.
const PALETTE_SIZE = 21

// Presence in this map decides the column shape: a listed mode renders a plain value
// column, an unlisted one falls back to a percentage progress bar.
const RESPONSES_TITLES = {
  attention: 'Time',
  time: 'Time',
  timeToFirstClick: 'Time',
  pVolume: 'Quantity',
  pAmount: 'Amount'
}

const SECOND_MODES = ['attention', 'time', 'timeToFirstClick']

const pct = percentDecimals => value => `${value.toFixed(percentDecimals ?? 1)}%`

const formatValue = (viewMode, priceTemplate) => value => {
  let out = ['pAmount', 'pVolume'].includes(viewMode) ? value.toFixed(2) : value.toFixed(1)

  if ('pAmount' === viewMode && priceTemplate) out = priceTemplate.replace(/{{price}}/gi, out)
  if (SECOND_MODES.includes(viewMode)) out += ` ${pluralize(value, 'second', 'seconds')}`

  return out
}

const calcCols = ({ viewMode, percentDecimals, priceTemplate, answer }) => {
  const cols = [{ type: 'title', title: 'Answers' }]

  if ('totalPickedUp' === viewMode) {
    cols.push(
      // the two shares that make up the total; the sort grammar can only name the products' own
      // order or the last column, so a click here would silently sort by the total instead
      { type: 'value', title: 'Purchased', format: pct(percentDecimals), sortable: false },
      { type: 'value', title: 'Picked up only', format: pct(percentDecimals), sortable: false },
      // only the share is known here, so the column names itself where the % marker would sit and
      // heads no count slot
      { type: 'progress', title: '', percentLabel: 'Total products picked up', percentDecimals, width: 200 }
    )
    return cols
  }

  if (RESPONSES_TITLES[viewMode]) {
    cols.push({
      type: 'value',
      title: RESPONSES_TITLES[viewMode],
      format: formatValue(viewMode, priceTemplate)
    })
    return cols
  }

  cols.push({
    type: 'progress',
    title: `Respondents <small>(${formatAmounts(answer, 'total')} total)</small>`,
    percentDecimals
  })
  return cols
}

// The answer variants keep their own order under an equal value, so a tie never shuffles the list.
const withSorts = cols =>
  cols.map(({ sortable, ...col }, index) => {
    if (false === sortable) return col
    if ('title' === col.type) return { ...col, sort: byPosition }

    return { ...col, sort: byCellValue(index, 'progress' === col.type ? 'percents' : 'value') }
  })

// The column the stored order names: the answers by their own order, or else the metric the
// chart is sorted by -- the last column, which is the value or the total.
const applySortedColumn = (cols, sortOrder) => {
  const order = String(sortOrder || DEFAULT_SORT_ORDER)

  const index = 'i' === order[0] ? 0 : cols.length - 1
  const direction = order.endsWith('asc') ? 1 : -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcChartTableProps = ({
  id,
  answer,
  answerVariants = [],
  viewMode,
  sortOrder,
  percentDecimals,
  priceTemplate,
  trait,
  traitName,
  isTraitType,
  clickable,
  onLabelClick,
  onSort
}) => {
  const isLoading = 'loading' === answer
  const values = isLoading || answer == undefined ? {} : answer

  const traitValues = getTraitValues(trait)
  const cols = calcCols({ viewMode, percentDecimals, priceTemplate, answer: values })

  const rows = answerVariants.map(av => {
    const a = values[`a${av.position}`] || {}

    const cells = [
      {
        title: processText(av.text),
        label: `A${av.position}`,
        colorIndex: trait && isTraitType ? 0 : ((av.position - 1) % PALETTE_SIZE) + 1
      }
    ]

    if ('totalPickedUp' === viewMode) {
      cells.push(
        { value: a.percents || 0, loading: isLoading },
        { value: a.pickedUpPercents || 0, loading: isLoading },
        { percents: (a.percents || 0) + (a.pickedUpPercents || 0), loading: isLoading }
      )
    } else {
      const amount = a[viewMode] || 0
      if (RESPONSES_TITLES[viewMode]) {
        cells.push({ value: amount, loading: isLoading })
      } else if ('amount' === viewMode) {
        // Purchase selection is a share of the respondents; the count beside it would only restate
        // the figure the bar already draws
        cells.push({ percents: a.percents || 0, loading: isLoading })
      } else {
        cells.push({ amount, percents: a.percents || 0, loading: isLoading })
      }
    }

    if (Object.hasOwn(av, 'image')) cells[0].image = av.image
    if (clickable) cells[0].onLabelClick = onLabelClick

    const row = { cells, position: av.position }

    if (trait && isTraitType) {
      const traitAnswer = a[traitName]
      if (traitAnswer !== undefined) {
        row.table = calcTraitTable({
          id,
          trait,
          traitName,
          traitValues,
          traitAnswer,
          viewMode,
          priceTemplate,
          responsesTitle: RESPONSES_TITLES[viewMode],
          percentDecimals,
          productTotalPickedUp: a.totalPickedUp,
          answerPosition: av.position
        })
      }
    }

    return row
  })

  return { cols: applySortedColumn(withSorts(cols), sortOrder), rows, onSort }
}
